import moment from "moment";
import UserServices from "../../../UserServices/UserServices";
import { emptyMealPlanIntakeForm, normalizeMealPlanIntakeForm } from "./mealPlanIntakeFormData";

const calcAge = (birthday) => {
  if (!birthday) return "";
  const b = moment(birthday);
  if (!b.isValid()) return "";
  return String(moment().diff(b, "years"));
};

const formatDate = (value) => {
  if (!value) return "";
  const d = moment(value);
  return d.isValid() ? d.format("YYYY-MM-DD") : String(value);
};

const calcBmi = (heightCm, weightKg) => {
  const h = parseFloat(heightCm);
  const w = parseFloat(weightKg);
  if (!h || !w) return "";
  const meters = h / 100;
  const bmi = w / (meters * meters);
  return Number.isFinite(bmi) ? bmi.toFixed(1) : "";
};

const joinParts = (parts) =>
  parts.filter((part) => part != null && String(part).trim() !== "").join(" | ");

export async function fetchClientIntakeDefaults(clientId, overrides = {}) {
  const base = emptyMealPlanIntakeForm();
  if (!clientId) return normalizeMealPlanIntakeForm({ ...base, ...overrides });

  let name = "";
  let age = "";
  let gender = "";
  let birthday = "";
  let phone = "";
  let email = "";
  let height = "";
  let weight = "";
  let bmi = "";
  let bodyFat = "";
  let lifestyle = "";
  let foodAllergy = "";
  let goals = "";
  let medicalHistory = "";

  try {
    const profileRes = await UserServices.getClientProfileInfo(clientId);
    if (profileRes.status === 200 && profileRes.data.data?.length) {
      const c = profileRes.data.data[0];
      name = `${c.first_name || ""} ${c.last_name || ""}`.trim();
      gender = c.gender || "";
      age = calcAge(c.birthday);
      birthday = formatDate(c.birthday);
      phone = c.phone || c.emergency_contact_no || "";
      email = c.email || "";
      lifestyle = joinParts([
        phone ? `Contact: ${phone}` : "",
        email ? `Email: ${email}` : "",
        c.city ? `City: ${c.city}` : "",
      ]);
    }
  } catch (err) {
    console.log(err);
  }

  try {
    const preRes = await UserServices.getAssessmentDetailsById(clientId);
    if (preRes.status === 200 && preRes.data.data?.length) {
      const pre = preRes.data.data[0];
      if (pre.height) height = String(pre.height);
      if (pre.weight && !weight) weight = String(pre.weight);
      if (pre.allergic_foods) foodAllergy = pre.allergic_foods;
      if (pre.fitness_goals) goals = pre.fitness_goals;
      if (pre.activity_level && !lifestyle.includes("Activity")) {
        lifestyle = joinParts([lifestyle, `Activity: ${pre.activity_level}`]);
      }
      if (pre.disliked_foods) {
        medicalHistory = joinParts([
          medicalHistory,
          pre.disliked_foods ? `Disliked foods: ${pre.disliked_foods}` : "",
        ]);
      }
    }
  } catch (err) {
    console.log(err);
  }

  try {
    const measureRes = await UserServices.getMeasurementsById(clientId);
    if (measureRes.status === 200 && measureRes.data.data?.length) {
      const latest = measureRes.data.data[0];
      if (latest.weight) weight = String(latest.weight);
      if (latest.body_mass_index) bmi = String(latest.body_mass_index);
      if (latest.fat) bodyFat = String(latest.fat);
    }
  } catch (err) {
    console.log(err);
  }

  if (!bmi) {
    bmi = calcBmi(height, weight);
  }

  const ageGenderFormatted =
    age && gender
      ? `${age} / ${gender}${birthday ? ` (DOB: ${birthday})` : ""}`
      : joinParts([
          age ? `${age} yrs` : "",
          gender,
          birthday ? `DOB: ${birthday}` : "",
        ]);

  return normalizeMealPlanIntakeForm({
    ...base,
    name,
    age_gender: ageGenderFormatted,
    height,
    weight,
    bmi,
    body_fat_pct: bodyFat,
    lifestyle,
    assessment_date: moment().format("YYYY-MM-DD"),
    medical_history: medicalHistory,
    food_allergy: foodAllergy,
    goals,
    ...overrides,
  });
}

export function resolveClientIdFromInput(inputValue, clients = []) {
  const match = clients.find(
    (client) => client.name.trim().toLowerCase() === inputValue.trim().toLowerCase()
  );
  return match?.id ? String(match.id) : "";
}
